import { formatDate, NgIf, NgFor, AsyncPipe } from '@angular/common';
import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatSelect, MatSelectChange, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import {
  PageDetailComponent,
  PrintPageComponent,
  NotificationEmptyComponent,
} from '@next/core-lib/components';
import { DialogService } from '@next/core-lib/dialog';
import {
  Currency,
  LocaleService,
  CurrencySymbolPipe,
  TranslatePipe,
  AgrDatePipe,
  AgrNumberPipe,
} from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { TableColumn, TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { AgrUser, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { debounceTime, map, skipWhile, take, withLatestFrom } from 'rxjs/operators';
import { FeedAdviceService } from 'src/app/feed-advice/services/feed-advice.service';

import {
  getCustomerProducts,
  getProducts,
} from '../../../../product/store/selectors/product.selectors';
import {
  DosatorProduct,
  DosatorSetting,
  EnergySystem,
  LysineSystem,
} from '../../../../settings/models/settings.model';
import {
  getDosatorSettings,
  getEnergySetting,
  getLysineSetting,
} from '../../../../settings/store/selectors/settings.selectors';
import { Product } from '../../../../shared/models/shared.model';
import {
  FeedAdvice,
  FeedAdviceProperties,
  FeedAdviceType,
  GeneticLineType,
  InputSystemType,
  ParityRegistration,
  ParityType,
  ProductTotalsSows,
  SowFeedAdviceLine,
  SowFeedadviceTableRow,
} from '../../../models/feed-advice.model';
import * as actions from '../../../store/feed-advice.actions';
import { upsertFeedAdviceRequest } from '../../../store/feed-advice.actions';
import {
  getSelectedCountry,
  getSelectedFeedAdvice,
  getSelectedFeedAdviceType,
  getSelectedLocations,
  getShowEnergySetting,
  getShowLysineSetting,
} from '../../../store/feed-advice.selectors';
import { connectingDayValidator } from '../../../validators/connecting-day.validator';
import { dayValidator } from '../../../validators/day.validator';
import { firstDayValidator } from '../../../validators/first-day.validator';
import { FeedAdviceSettingsComponent } from '../../feed-advice-settings/feed-advice-settings.component';
import { PriceSettingsComponent } from '../../price-settings/price-settings.component';
import { SowsFeedAdviceGraphComponent } from '../sows-feed-advice-graph/sows-feed-advice-graph.component';
import { SowsFeedAdvicePropertiesComponent } from '../sows-feed-advice-properties/sows-feed-advice-properties.component';

import { SowsFeedAdviceReportComponent } from '../sows-feed-advice-report/sows-feed-advice-report.component';

@Component({
  selector: 'app-sows-feed-advice-detail',
  templateUrl: './sows-feed-advice-detail.component.html',
  styleUrls: ['./sows-feed-advice-detail.component.scss'],
  imports: [
    PageDetailComponent,
    NgIf,
    MatSelect,
    MatOption,
    MatButton,
    MatIcon,
    PrintPageComponent,
    SowsFeedAdviceReportComponent,
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    FormsModule,
    FeedAdviceSettingsComponent,
    MatRadioGroup,
    MatRadioButton,
    NgFor,
    MatSlideToggle,
    TableComponent,
    NotificationEmptyComponent,
    SowsFeedAdviceGraphComponent,
    AsyncPipe,
    CurrencySymbolPipe,
    TranslatePipe,
    AgrDatePipe,
    AgrNumberPipe,
  ],
})
export class SowsFeedAdviceDetailComponent implements OnInit, AfterViewInit, AfterContentChecked {
  pageTitle$: Observable<string> = this.store.select(RouterSelectors.getPath).pipe(
    map((route: string) => {
      if (route.indexOf('/create') !== -1) {
        return this.locale.translate(`sows-feed-advice.${this.feedAdviceType}-create`);
      } else if (route.indexOf('/view') !== -1) {
        return this.locale.translate(`sows-feed-advice.${this.advice?.type}-view`);
      } else if (route.indexOf('/edit') !== -1) {
        return this.locale.translate(`sows-feed-advice.${this.advice?.type}-edit`);
      }
    }),
  );
  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);
  location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);
  energySetting$: Observable<EnergySystem> = this.store.select(getEnergySetting);
  lysineSetting$: Observable<LysineSystem> = this.store.select(getLysineSetting);
  dosatorSettings$: Observable<DosatorSetting[]> = this.store.select(getDosatorSettings);

  selectedFeedAdviceType$: Observable<FeedAdviceType> =
    this.store.select(getSelectedFeedAdviceType);

  products$: Observable<Product[]> = this.store.select(getProducts).pipe(
    withLatestFrom(this.store.select(getCustomerProducts)),
    skipWhile(
      ([existingProducts, customerProducts]) =>
        existingProducts === null || customerProducts === null,
    ),
    map(([products, customerProducts]: [Product[], Product[]]) =>
      [...(products ?? []), ...(customerProducts ?? [])]
        .filter((p) => p.schemeTypes.includes(this.feedAdviceType))
        .map(
          (product) =>
            ({
              id: product.id,
              code: product.code,
              name: product.name,
              suitablePhases: product.suitablePhases,
              ewKg: product.ewKg,
              lysKg: product.lysKg,
              customerId: product.customerId,
              country: product.country,
              energySystem: product.energySystem,
              lysineSystem: product.lysineSystem,
              unitNettoPrice: product.unitNettoPrice,
              currency: product.currency,
              schemeTypes: product.schemeTypes,
            }) as Product,
        ),
    ),
    take(1),
  );

  filteredProducts: Product[];
  filterProductsValue = '';

  dayFromArray = new FormArray([]);
  dayToArray = new FormArray([]);
  productArray = new FormArray([]);
  feedingTimesPerDayArray = new FormArray([]);
  energyArrays: Array<unknown> = [];
  feedArrays: Array<unknown> = [];
  lysineArrays: Array<unknown> = [];
  dosatorArrays: Array<unknown> = [];
  private tableColumns: TableColumn[];
  private displayedTableColumns: TableColumn[];

  productTotals: ProductTotalsSows[] = [];
  sowFeedAdviceLines: SowFeedAdviceLine[] = [];
  currency: Currency = null;

  energySystem: EnergySystem;
  lysineSystem: LysineSystem;

  tableConfig: TableConfig;
  isReferenceFeedAdvice: boolean;
  mode: string;
  feedAdviceType: FeedAdviceType;
  feedAdviceProperties: FeedAdviceProperties;
  useParitySystem = true;
  parityCategory: ParityType = ParityType.ONETOTWO;
  inputSystem: InputSystemType = InputSystemType.ENERGYDAY;
  showLysineColumns: boolean;
  showEnergyColumns: boolean;
  showDosatorColumns: boolean;
  printSetting = 'fullsize';
  advice: FeedAdvice;
  dosatorSettings: DosatorSetting[];
  selectedDosatorSetting: DosatorSetting;
  errorsDescription: string;

  transition: {
    day?: number;
  } = {};

  changeNumberOfTableRowsClickStream$ = new Subject<string>();

  @ViewChild('productNameTemplate') productNameTemplate: TemplateRef<Product>;
  @ViewChild(SowsFeedAdviceGraphComponent) graphSowsComponent: SowsFeedAdviceGraphComponent;
  @ViewChild(SowsFeedAdvicePropertiesComponent)
  sowsFeedAdvicePropertiesComponent: SowsFeedAdvicePropertiesComponent;
  @ViewChild('dosatorSettingSelector') dosatorSettingSelector: MatSelect;

  constructor(
    private readonly store: Store<RootState>,
    private readonly activatedRoute: ActivatedRoute,
    private readonly dialogService: DialogService,
    private readonly router: Router,
    private cdref: ChangeDetectorRef,
    private readonly locale: LocaleService,
    private readonly feedAdviceService: FeedAdviceService,
  ) {
    this.activatedRoute.url.subscribe((urlSegments) => {
      this.mode = urlSegments[0]?.path;
    });
    this.activatedRoute.params.subscribe((params) => {
      this.feedAdviceType = <FeedAdviceType>params.feedAdviceType;
    });
    this.activatedRoute.data.subscribe((response) => {
      this.isReferenceFeedAdvice = <boolean>response.isReferenceFeedAdvice;
    });
    this.changeNumberOfTableRowsClickStream$.pipe(debounceTime(200)).subscribe((operation) => {
      this.recalculateNumberOfTableRows(operation);
    });

    this.store
      .select(getShowLysineSetting)
      .pipe(
        take(1),
        skipWhile((showLysine) => showLysine === null),
      )
      .subscribe((showLysine) => {
        this.showLysineColumns = showLysine ?? true;
      });

    this.store
      .select(getShowEnergySetting)
      .pipe(
        take(1),
        skipWhile((showEnergy) => showEnergy === null),
      )
      .subscribe((showEnergy) => {
        this.showEnergyColumns = showEnergy ?? true;
      });
  }

  formGroupParityAlternative: FormGroup;

  get parityAlternative2FormControl(): AbstractControl {
    return this.formGroupParityAlternative.get('parityAlternative2');
  }

  get parityAlternative3FormControl(): AbstractControl {
    return this.formGroupParityAlternative.get('parityAlternative3');
  }

  get parityAlternative4FormControl(): AbstractControl {
    return this.formGroupParityAlternative.get('parityAlternative4');
  }

  get parityAlternative5FormControl(): AbstractControl {
    return this.formGroupParityAlternative.get('parityAlternative5');
  }

  get parityAlternative6FormControl(): AbstractControl {
    return this.formGroupParityAlternative.get('parityAlternative6');
  }

  ngOnInit() {
    const getDatasourceLine = (line: SowFeedAdviceLine, index: number): SowFeedadviceTableRow => ({
      id: index,
      dayFrom: line.dayFrom,
      dayTo: line.dayTo,
      energyday_input_1: line.parityRegistrations.find((pr) => pr.parity === 1)?.ewKg,
      energyday_input_2: line.parityRegistrations.find((pr) => pr.parity === 2)?.ewKg,
      energyday_input_3: line.parityRegistrations.find((pr) => pr.parity === 3)?.ewKg,
      energyday_input_4: line.parityRegistrations.find((pr) => pr.parity === 4)?.ewKg,
      energyday_input_5: line.parityRegistrations.find((pr) => pr.parity === 5)?.ewKg,
      energyday_readonly_1: line.parityRegistrations.find((pr) => pr.parity === 1)?.ewKg,
      energyday_readonly_2: line.parityRegistrations.find((pr) => pr.parity === 2)?.ewKg,
      energyday_readonly_3: line.parityRegistrations.find((pr) => pr.parity === 3)?.ewKg,
      energyday_readonly_4: line.parityRegistrations.find((pr) => pr.parity === 4)?.ewKg,
      energyday_readonly_5: line.parityRegistrations.find((pr) => pr.parity === 5)?.ewKg,
      product: line.product,
      kgday_input_1: line.parityRegistrations.find((pr) => pr.parity === 1)?.feedKg,
      kgday_input_2: line.parityRegistrations.find((pr) => pr.parity === 2)?.feedKg,
      kgday_input_3: line.parityRegistrations.find((pr) => pr.parity === 3)?.feedKg,
      kgday_input_4: line.parityRegistrations.find((pr) => pr.parity === 4)?.feedKg,
      kgday_input_5: line.parityRegistrations.find((pr) => pr.parity === 5)?.feedKg,
      kgday_readonly_1: line.parityRegistrations.find((pr) => pr.parity === 1)?.feedKg,
      kgday_readonly_2: line.parityRegistrations.find((pr) => pr.parity === 2)?.feedKg,
      kgday_readonly_3: line.parityRegistrations.find((pr) => pr.parity === 3)?.feedKg,
      kgday_readonly_4: line.parityRegistrations.find((pr) => pr.parity === 4)?.feedKg,
      kgday_readonly_5: line.parityRegistrations.find((pr) => pr.parity === 5)?.feedKg,
      lysine_readonly_1: line.parityRegistrations.find((pr) => pr.parity === 1)?.lysKg,
      lysine_readonly_2: line.parityRegistrations.find((pr) => pr.parity === 2)?.lysKg,
      lysine_readonly_3: line.parityRegistrations.find((pr) => pr.parity === 3)?.lysKg,
      lysine_readonly_4: line.parityRegistrations.find((pr) => pr.parity === 4)?.lysKg,
      lysine_readonly_5: line.parityRegistrations.find((pr) => pr.parity === 5)?.lysKg,
      feedingTimesPerDay: line.feedingTimesPerDay,
      dosator_input_1: line.parityRegistrations.find((pr) => pr.parity === 1)?.dosatorPosition,
      dosator_input_2: line.parityRegistrations.find((pr) => pr.parity === 2)?.dosatorPosition,
      dosator_input_3: line.parityRegistrations.find((pr) => pr.parity === 3)?.dosatorPosition,
      dosator_input_4: line.parityRegistrations.find((pr) => pr.parity === 4)?.dosatorPosition,
      dosator_input_5: line.parityRegistrations.find((pr) => pr.parity === 5)?.dosatorPosition,
      dosator_readonly_input_1: line.parityRegistrations.find((pr) => pr.parity === 1)
        ?.dosatorPosition,
      dosator_readonly_input_2: line.parityRegistrations.find((pr) => pr.parity === 2)
        ?.dosatorPosition,
      dosator_readonly_input_3: line.parityRegistrations.find((pr) => pr.parity === 3)
        ?.dosatorPosition,
      dosator_readonly_input_4: line.parityRegistrations.find((pr) => pr.parity === 4)
        ?.dosatorPosition,
      dosator_readonly_input_5: line.parityRegistrations.find((pr) => pr.parity === 5)
        ?.dosatorPosition,
    });
    this.store
      .select(getSelectedFeedAdvice)
      .pipe(
        skipWhile((selectedFeedAdvice) => this.mode !== 'create' && !selectedFeedAdvice),
        withLatestFrom(
          this.store.select(UserSelectors.selectUser),
          this.store.select(getSelectedCountry),
          this.dosatorSettings$,
          this.energySetting$,
          this.lysineSetting$,
        ),
        take(1),
      )
      .subscribe(
        ([selectedFeedAdvice, user, country, dosatorSettings, energySetting, lysineSetting]: [
          FeedAdvice,
          AgrUser,
          string,
          DosatorSetting[],
          EnergySystem,
          LysineSystem,
        ]) => {
          this.energySystem = energySetting;
          this.lysineSystem = lysineSetting;
          this.dosatorSettings = dosatorSettings;
          this.createInitialAdvice(selectedFeedAdvice, user, country);

          const tableConfigBase: TableConfig = {
            dataSource: new MatTableDataSource([] as SowFeedadviceTableRow[]),
            columns: undefined,
            selectColumn: false,
            actionColumn: false,
            pagination: false,
            disableCards: true,
            enableArrowKeys: true,
            footer: false,
            scrolling: true,
            wrapperWidth: '100%',
          };
          // TABLECONFIG
          this.getTableColumns();
          const initialDatasource: SowFeedadviceTableRow[] = [];
          this.advice.sowFeedAdviceLines.forEach((line, index) =>
            initialDatasource.push(getDatasourceLine(line, index)),
          );
          this.tableConfig = <TableConfig>{
            ...tableConfigBase,
            columns: this.displayedTableColumns,
            dataSource: new MatTableDataSource<SowFeedadviceTableRow>(initialDatasource),
          };

          this.sowFeedAdviceLines = this.advice.sowFeedAdviceLines;
          this.productTotals = this.feedAdviceService.calculateProductTotalsSows(
            this.sowFeedAdviceLines,
          );
          this.currency = this.locale.getCurrency();

          this.useParitySystem = this.advice?.useParitySystem ?? true;
          this.formGroupParityAlternative = new FormGroup({
            parityAlternative2: new FormControl(this.advice?.parityAlternative2),
            parityAlternative3: new FormControl(this.advice?.parityAlternative3),
            parityAlternative4: new FormControl(this.advice?.parityAlternative4),
            parityAlternative5: new FormControl(this.advice?.parityAlternative5),
            parityAlternative6: new FormControl(this.advice?.parityAlternative6),
          });

          this.formGroupParityAlternative.valueChanges.subscribe(() => {
            this.advice.parityAlternative2 =
              this.formGroupParityAlternative.value.parityAlternative2;
            this.advice.parityAlternative3 =
              this.formGroupParityAlternative.value.parityAlternative3;
            this.advice.parityAlternative4 =
              this.formGroupParityAlternative.value.parityAlternative4;
            this.advice.parityAlternative5 =
              this.formGroupParityAlternative.value.parityAlternative5;
            this.advice.parityAlternative6 =
              this.formGroupParityAlternative.value.parityAlternative6;

            this.setParitySystem();
          });

          if (this.mode !== 'create') {
            this.setParitySystem(false);
          }

          return this.tableConfig;
        },
      );

    this.resetFilterProducts();
  }

  setPrintSetting(event: MatSelectChange) {
    this.printSetting = <string>event.value;
  }

  updateSummary() {
    this.sowFeedAdviceLines = this.getSowFeedAdviceLines();
    this.productTotals = this.feedAdviceService.calculateProductTotalsSows(this.sowFeedAdviceLines);
    if (this.graphSowsComponent) {
      this.graphSowsComponent.lines = this.sowFeedAdviceLines;
      this.graphSowsComponent.changeSetting();
    }
  }

  openPriceSettings() {
    this.dialogService
      .form(PriceSettingsComponent, {
        products: this.sowFeedAdviceLines
          .map((p) => p.product)
          .filter((p) => p && Object.keys(p).length > 0)
          .filter(
            (product, index, self) =>
              index === self.findIndex((p) => p.code === product.code && p.name === product.name),
          ),
        feedAdvice: this.advice,
      })
      .subscribe((result) => {
        if (result.result?.products) {
          this.productArray.controls.forEach((c) => {
            const product = result.result?.products?.find((p) => p.id === c.value.id);
            c.patchValue({
              ...c.value,
              unitNettoPrice: product.unitNettoPrice,
              currency: product.currency,
            });
          });

          this.productTotals.forEach((pt, index) => {
            const product = result.result?.products?.find((p) => p.code === pt.productCode);
            this.productTotals[index] = {
              ...this.productTotals[index],
              unitNettoPrice: product.unitNettoPrice,
            };
          });

          this.updateFeedadviceRows();
        }
      });
  }

  hasNettoPrices(): boolean {
    return this.productTotals.some((pt) => !!pt.unitNettoPrice && pt.unitNettoPrice > 0);
  }

  getTotalNettoPrice(feedKg: number, unitNettoPrice: number) {
    return (feedKg / 100) * unitNettoPrice;
  }

  getSumTotalNettoPrice(index: number) {
    let sum = 0;
    this.productTotals.forEach(
      (pt) => (sum += this.getTotalNettoPrice(pt.parityTotals[index].feedKg, pt.unitNettoPrice)),
    );
    return sum;
  }

  getTotalFeed(index: number) {
    let sum = 0;
    this.productTotals.forEach((pt) => (sum += pt.parityTotals[index].feedKg));
    return sum;
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const newConfig = { ...this.tableConfig };
      if (newConfig.columns) {
        const column = newConfig.columns.find((c) => c.id === 'product');
        column.template = this.productNameTemplate;
        this.tableConfig = newConfig;

        // the FormArray that is set to readonly, will not be populated with data on initialization.
        // We have to trigger this manually
        this.setParityAndInputSystem();
      }
    }, 50);
  }

  printFeedAdvice() {
    const existingTitle = document.title;
    const proposedTitle = `Pig ${this.locale.translate('feed-advice-title')} - ${formatDate(
      Date(),
      'dd-MM-yyyy',
      this.locale.getLocale(),
    )}`;
    if (window) {
      document.title = proposedTitle;
      window.print();
      document.title = existingTitle;
    }
  }

  getButtonText(type: string): string {
    return this.locale.translate(
      `${this.isReferenceFeedAdvice ? 'reference-' : ''}feed-advice-${type}`,
    );
  }

  back() {
    this.router.navigate([`/${this.isReferenceFeedAdvice ? 'reference-' : ''}feed-advice`]);
  }

  private getTableColumns() {
    const getControls = (): FormControl[] => {
      const controls: FormControl[] = [];
      this.advice.sowFeedAdviceLines.forEach((_) => controls.push(new FormControl()));
      return controls;
    };
    for (let parity = 1; parity <= 5; parity++) {
      this.energyArrays[parity - 1] = new FormArray(getControls());
      this.feedArrays[parity - 1] = new FormArray(getControls());
      this.lysineArrays[parity - 1] = new FormArray(getControls());
      this.dosatorArrays[parity - 1] = new FormArray(getControls());
    }
    this.productArray = new FormArray(getControls());
    this.feedingTimesPerDayArray = new FormArray(getControls());

    this.feedAdviceType = this.feedAdviceType ? this.feedAdviceType : this.advice.feedAdviceType;

    let calculation;
    switch (this.feedAdviceType) {
      case FeedAdviceType.SOWS_BREEDTOFARROW:
        calculation = this.locale.translate('settings.energy.short.E-Dracht');
        break;
      case FeedAdviceType.SOWS_FARROWTOWEAN:
        calculation = this.locale.translate('settings.energy.short.E-Lacto');
        break;
      case FeedAdviceType.SOWS_WEANTOBREED:
        calculation = this.locale.translate('settings.energy.short.E-Lacto');
        break;
      default:
        calculation = this.locale.translate('settings.energy.short.' + this.energySystem);
    }

    this.tableColumns = [
      {
        id: 'dayFrom',
        type: TableColumnTypes.INPUT_NUMBER,
        label: this.locale.translate('sows-feed-advice.table.dayFrom'),
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.dayFromArray,
          step: 1,
          options: [
            dayValidator(
              'dayFrom',
              this.dayFromArray,
              this.dayToArray,
              this.feedAdviceType,
              this.transition,
            ),
            firstDayValidator(this.dayFromArray, this.feedAdviceType, this.transition),
            connectingDayValidator(
              'dayFrom',
              this.dayFromArray,
              this.dayToArray,
              this.feedAdviceType,
            ),
          ],
        },
      },
      {
        id: 'dayTo',
        type: TableColumnTypes.INPUT_NUMBER,
        label: this.locale.translate('sows-feed-advice.table.dayTo'),
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.dayToArray,
          step: 1,
          options: [
            dayValidator(
              'dayTo',
              this.dayFromArray,
              this.dayToArray,
              this.feedAdviceType,
              this.transition,
            ),
            connectingDayValidator(
              'dayTo',
              this.dayFromArray,
              this.dayToArray,
              this.feedAdviceType,
            ),
          ],
        },
      },
      {
        id: 'energyday_input_1',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.energy')}
          (${calculation}/d) ${this.locale.translate('parity')} 1`,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.energyArrays[0],
          step: 0.01,
        },
      },
      {
        id: 'energyday_input_2',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.energy',
        )}  (${calculation}/d) ${this.locale.translate('parity')} 2`,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.energyArrays[1],
          step: 0.01,
        },
      },
      {
        id: 'energyday_input_3',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.energy',
        )}  (${calculation}/d) ${this.locale.translate('parity')} 3`,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.energyArrays[2],
          step: 0.01,
        },
      },
      {
        id: 'energyday_input_4',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.energy',
        )}  (${calculation}/d) ${this.locale.translate('parity')} 4`,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.energyArrays[3],
          step: 0.01,
        },
      },
      {
        id: 'energyday_input_5',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.energy',
        )}  (${calculation}/d) ${this.locale.translate('parity')} >= 5`,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.energyArrays[4],
          step: 0.01,
        },
      },
      {
        id: 'energyday_readonly_1',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.energy',
        )}  (${calculation}/d) ${this.locale.translate('parity')} 1`,
        digitsInfo: '1.02-02',
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.energyArrays[0],
        },
      },
      {
        id: 'energyday_readonly_2',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.energy',
        )}  (${calculation}/d) ${this.locale.translate('parity')} 2`,
        disableSort: true,
        digitsInfo: '1.02-02',
        inputConfig: {
          formArray: <FormArray>this.energyArrays[1],
        },
      },
      {
        id: 'energyday_readonly_3',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.energy',
        )}  (${calculation}/d) ${this.locale.translate('parity')} 3`,
        disableSort: true,
        digitsInfo: '1.02-02',
        inputConfig: {
          formArray: <FormArray>this.energyArrays[2],
        },
      },
      {
        id: 'energyday_readonly_4',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.energy',
        )}  (${calculation}/d) ${this.locale.translate('parity')} 4`,
        disableSort: true,
        digitsInfo: '1.02-02',
        inputConfig: {
          formArray: <FormArray>this.energyArrays[3],
        },
      },
      {
        id: 'energyday_readonly_5',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.energy',
        )}  (${calculation}/d) ${this.locale.translate('parity')}  >= 5`,
        disableSort: true,
        digitsInfo: '1.02-02',
        inputConfig: {
          formArray: <FormArray>this.energyArrays[4],
        },
      },
      {
        id: 'product',
        type: TableColumnTypes.TEMPLATE,
        label: this.locale.translate('sows-feed-advice.table.product'),
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.productArray,
        },
      },
      {
        id: 'kgday_input_1',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.feed',
        )} (kg/d) ${this.locale.translate('parity')} 1`,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.feedArrays[0],
          step: 0.01,
        },
      },
      {
        id: 'kgday_input_2',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.feed',
        )} (kg/d) ${this.locale.translate('parity')} 2`,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.feedArrays[1],
          step: 0.01,
        },
      },
      {
        id: 'kgday_input_3',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.feed',
        )} (kg/d) ${this.locale.translate('parity')} 3`,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.feedArrays[2],
          step: 0.01,
        },
      },
      {
        id: 'kgday_input_4',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.feed',
        )} (kg/d) ${this.locale.translate('parity')} 4`,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.feedArrays[3],
          step: 0.01,
        },
      },
      {
        id: 'kgday_input_5',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.feed',
        )} (kg/d) ${this.locale.translate('parity')} >= 5`,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.feedArrays[4],
          step: 0.01,
        },
      },
      {
        id: 'kgday_readonly_1',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.feed',
        )} (kg/d) ${this.locale.translate('parity')} 1`,
        disableSort: true,
        digitsInfo: '1.02-02',
        inputConfig: {
          formArray: <FormArray>this.feedArrays[0],
        },
      },
      {
        id: 'kgday_readonly_2',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.feed',
        )} (kg/d) ${this.locale.translate('parity')} 2`,
        disableSort: true,
        digitsInfo: '1.02-02',
        inputConfig: {
          formArray: <FormArray>this.feedArrays[1],
        },
      },
      {
        id: 'kgday_readonly_3',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.feed',
        )} (kg/d) ${this.locale.translate('parity')} 3`,
        disableSort: true,
        digitsInfo: '1.02-02',
        inputConfig: {
          formArray: <FormArray>this.feedArrays[2],
        },
      },
      {
        id: 'kgday_readonly_4',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.feed',
        )} (kg/d) ${this.locale.translate('parity')} 4`,
        disableSort: true,
        digitsInfo: '1.02-02',
        inputConfig: {
          formArray: <FormArray>this.feedArrays[3],
        },
      },
      {
        id: 'kgday_readonly_5',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate(
          'sows-feed-advice.table.feed',
        )} (kg/d) ${this.locale.translate('parity')} >= 5`,
        disableSort: true,
        digitsInfo: '1.02-02',
        inputConfig: {
          formArray: <FormArray>this.feedArrays[4],
        },
      },
      {
        id: 'feedingTimesPerDay',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.feedingTimesPerDay')}`,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.feedingTimesPerDayArray,
          step: 1,
          callback: (row: SowFeedadviceTableRow, input) =>
            this.editFeedingTimesRow(row, <number>input.value),
        },
      },
      {
        id: 'dosator_input_1',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.dosator')} ${this.locale.translate(
          'parity',
        )} 1`,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.dosatorArrays[0],
          step: 0.1,
        },
      },
      {
        id: 'dosator_input_2',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.dosator')} ${this.locale.translate(
          'parity',
        )} 2`,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.dosatorArrays[1],
          step: 0.1,
        },
      },
      {
        id: 'dosator_input_3',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.dosator')} ${this.locale.translate(
          'parity',
        )} 3`,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.dosatorArrays[2],
          step: 0.1,
        },
      },
      {
        id: 'dosator_input_4',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.dosator')} ${this.locale.translate(
          'parity',
        )} 4`,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.dosatorArrays[3],
          step: 0.1,
        },
      },
      {
        id: 'dosator_input_5',
        type: TableColumnTypes.INPUT_NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.dosator')} ${this.locale.translate(
          'parity',
        )} >= 5`,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.dosatorArrays[4],
          step: 0.1,
        },
      },
      {
        id: 'dosator_readonly_input_1',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.dosator')} ${this.locale.translate(
          'parity',
        )} 1`,
        disableSort: true,
        digitsInfo: '1.01-01',
        inputConfig: {
          formArray: <FormArray>this.dosatorArrays[0],
        },
      },
      {
        id: 'dosator_readonly_input_2',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.dosator')} ${this.locale.translate(
          'parity',
        )} 2`,
        disableSort: true,
        digitsInfo: '1.01-01',
        inputConfig: {
          formArray: <FormArray>this.dosatorArrays[1],
        },
      },
      {
        id: 'dosator_readonly_input_3',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.dosator')} ${this.locale.translate(
          'parity',
        )} 3`,
        disableSort: true,
        digitsInfo: '1.01-01',
        inputConfig: {
          formArray: <FormArray>this.dosatorArrays[2],
        },
      },
      {
        id: 'dosator_readonly_input_4',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.dosator')} ${this.locale.translate(
          'parity',
        )} 4`,
        disableSort: true,
        digitsInfo: '1.01-01',
        inputConfig: {
          formArray: <FormArray>this.dosatorArrays[3],
        },
      },
      {
        id: 'dosator_readonly_input_5',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.dosator')} ${this.locale.translate(
          'parity',
        )} >= 5`,
        disableSort: true,
        digitsInfo: '1.01-01',
        inputConfig: {
          formArray: <FormArray>this.dosatorArrays[4],
        },
      },

      {
        id: 'lysine_readonly_1',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.lysine')} (${
          this.lysineSystem
        }/d) ${this.locale.translate('parity')} 1`,
        disableSort: true,
        digitsInfo: '1.01-01',
        inputConfig: {
          formArray: <FormArray>this.lysineArrays[0],
        },
      },
      {
        id: 'lysine_readonly_2',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.lysine')} (${
          this.lysineSystem
        }/d) ${this.locale.translate('parity')} 2`,
        disableSort: true,
        digitsInfo: '1.01-01',
        inputConfig: {
          formArray: <FormArray>this.lysineArrays[1],
        },
      },
      {
        id: 'lysine_readonly_3',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.lysine')} (${
          this.lysineSystem
        }/d) ${this.locale.translate('parity')} 3`,
        disableSort: true,
        digitsInfo: '1.01-01',
        inputConfig: {
          formArray: <FormArray>this.lysineArrays[2],
        },
      },
      {
        id: 'lysine_readonly_4',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.lysine')} (${
          this.lysineSystem
        }/d) ${this.locale.translate('parity')} 4`,
        disableSort: true,
        digitsInfo: '1.01-01',
        inputConfig: {
          formArray: <FormArray>this.lysineArrays[3],
        },
      },
      {
        id: 'lysine_readonly_5',
        type: TableColumnTypes.NUMBER,
        label: `${this.locale.translate('sows-feed-advice.table.lysine')} (${
          this.lysineSystem
        }/d) ${this.locale.translate('parity')} >= 5`,
        disableSort: true,
        digitsInfo: '1.01-01',
        inputConfig: {
          formArray: <FormArray>this.lysineArrays[4],
        },
      },
    ];
    this.getDisplayedTableColumns();
  }

  editFeedingTimesRow(tableRow: SowFeedadviceTableRow, inputValue: number): void {
    const tableData = this.tableConfig.dataSource.filteredData.slice();
    const rowIndex = tableData.findIndex((d: SowFeedadviceTableRow) => d.id === tableRow.id);
    tableData.forEach((d: SowFeedadviceTableRow, index: number) => {
      if (index > rowIndex) {
        this.feedingTimesPerDayArray.controls[d.id].setValue(inputValue);
      }
    });
  }

  /**
   * returns the tableColumns that are actually shown
   */
  private getDisplayedTableColumns() {
    const getLabel = (label: string): string => {
      switch (this.parityCategory) {
        case ParityType.ONETOTWO:
          return `${label} >= 2`;
        case ParityType.TWOTOFOUR:
          return `${label} 2-4`;
        case ParityType.ONETOFIVE:
          return `${label} 2`;
      }
    };
    switch (this.inputSystem) {
      case InputSystemType.KGDAY:
        this.displayedTableColumns = this.tableColumns
          .filter((column) => !column.id.startsWith('energyday_input'))
          .filter((column) => !column.id.startsWith('dosator_input'))
          .filter((column) => !column.id.startsWith('kgday_readonly'));
        break;
      case InputSystemType.ENERGYDAY:
        this.displayedTableColumns = this.tableColumns
          .filter((column) => !column.id.startsWith('energyday_readonly'))
          .filter((column) => !column.id.startsWith('dosator_input'))
          .filter((column) => !column.id.startsWith('kgday_input'));
        break;
      case InputSystemType.DOSATOR:
        this.displayedTableColumns = this.tableColumns
          .filter((column) => !column.id.startsWith('energyday_input'))
          .filter((column) => !column.id.startsWith('dosator_readonly'))
          .filter((column) => !column.id.startsWith('kgday_input'));
        break;
    }

    if (this.parityCategory !== ParityType.ONETOFIVE) {
      this.displayedTableColumns = this.displayedTableColumns.filter((column) => {
        const lastchar = Number(column.id.substr(column.id.length - 1, 1));
        if (isNaN(lastchar)) {
          return true;
        }
        if (this.parityCategory === ParityType.ONETOTWO) {
          return lastchar <= 2;
        } else if (this.parityCategory === ParityType.TWOTOFOUR) {
          return lastchar <= 3;
        } else if (this.parityCategory === ParityType.ONETOFOUR) {
          return lastchar <= 4;
        }
      });
    }

    let calculation;
    switch (this.feedAdviceType) {
      case FeedAdviceType.SOWS_BREEDTOFARROW:
        calculation = this.locale.translate('settings.energy.short.E-Dracht');
        break;
      case FeedAdviceType.SOWS_FARROWTOWEAN:
        calculation = this.locale.translate('settings.energy.short.E-Lacto');
        break;
      case FeedAdviceType.SOWS_WEANTOBREED:
        calculation = this.locale.translate('settings.energy.short.E-Lacto');
        break;
      default:
        calculation = this.locale.translate('settings.energy.short.' + this.energySystem);
    }

    let label = '';
    if (this.showEnergyColumns) {
      if (this.useParitySystem) {
        label = `${this.locale.translate(
          'sows-feed-advice.table.energy',
        )} (${calculation}/d) ${this.locale.translate('parity')}`;
        if (this.displayedTableColumns.findIndex((col) => col.id === 'energyday_input_2') !== -1) {
          this.displayedTableColumns.find((col) => col.id === 'energyday_input_2').label =
            getLabel(label);
          if (this.parityCategory !== ParityType.ONETOTWO) {
            this.displayedTableColumns.find((col) => col.id === 'energyday_input_3').label =
              this.parityCategory === ParityType.TWOTOFOUR ? `${label} >= 5` : `${label} 3`;
          }
        }
        if (
          this.displayedTableColumns.findIndex((col) => col.id === 'energyday_readonly_2') !== -1
        ) {
          this.displayedTableColumns.find((col) => col.id === 'energyday_readonly_2').label =
            getLabel(label);
          if (this.parityCategory !== ParityType.ONETOTWO) {
            this.displayedTableColumns.find((col) => col.id === 'energyday_readonly_3').label =
              this.parityCategory === ParityType.TWOTOFOUR ? `${label} >= 5` : `${label} 3`;
          }
        }
      }
    } else {
      this.displayedTableColumns = this.displayedTableColumns.filter(
        (column) => column.id.indexOf('energy') === -1,
      );
    }
    if (this.useParitySystem) {
      label = `${this.locale.translate(
        'sows-feed-advice.table.feed',
      )} (kg/d) ${this.locale.translate('parity')}`;
      if (this.displayedTableColumns.findIndex((col) => col.id === 'kgday_input_2') !== -1) {
        this.displayedTableColumns.find((col) => col.id === 'kgday_input_2').label =
          getLabel(label);
        if (this.parityCategory !== ParityType.ONETOTWO) {
          this.displayedTableColumns.find((col) => col.id === 'kgday_input_3').label =
            this.parityCategory === ParityType.TWOTOFOUR ? `${label} >= 5` : `${label} 3`;
        }
      }
      if (this.displayedTableColumns.findIndex((col) => col.id === 'kgday_readonly_2') !== -1) {
        this.displayedTableColumns.find((col) => col.id === 'kgday_readonly_2').label =
          getLabel(label);
        if (this.parityCategory !== ParityType.ONETOTWO) {
          this.displayedTableColumns.find((col) => col.id === 'kgday_readonly_3').label =
            this.parityCategory === ParityType.TWOTOFOUR ? `${label} >= 5` : `${label} 3`;
        }
      }
    }

    if (this.showLysineColumns) {
      if (this.useParitySystem) {
        label = `${this.locale.translate('sows-feed-advice.table.lysine')} (${
          this.lysineSystem
        }/d) ${this.locale.translate('parity')}`;
        this.displayedTableColumns.find((col) => col.id === 'lysine_readonly_2').label =
          getLabel(label);
        if (this.parityCategory !== ParityType.ONETOTWO) {
          this.displayedTableColumns.find((col) => col.id === 'lysine_readonly_3').label =
            this.parityCategory === ParityType.TWOTOFOUR ? `${label} >= 5` : `${label} 3`;
        }
      }
    } else {
      this.displayedTableColumns = this.displayedTableColumns.filter(
        (column) => column.id.indexOf('lysine') === -1,
      );
    }

    if (this.showDosatorColumns) {
      if (this.useParitySystem) {
        label = `${this.locale.translate('sows-feed-advice.table.dosator')} ${this.locale.translate(
          'parity',
        )}`;
        if (this.displayedTableColumns.findIndex((col) => col.id === 'dosator_input_2') !== -1) {
          this.displayedTableColumns.find((col) => col.id === 'dosator_input_2').label =
            getLabel(label);
          if (this.parityCategory !== ParityType.ONETOTWO) {
            this.displayedTableColumns.find((col) => col.id === 'dosator_input_3').label =
              this.parityCategory === ParityType.TWOTOFOUR ? `${label} >= 5` : `${label} 3`;
          }
        }
        if (
          this.displayedTableColumns.findIndex((col) => col.id === 'dosator_readonly_input_2') !==
          -1
        ) {
          this.displayedTableColumns.find((col) => col.id === 'dosator_readonly_input_2').label =
            getLabel(label);
          if (this.parityCategory !== ParityType.ONETOTWO) {
            this.displayedTableColumns.find((col) => col.id === 'dosator_readonly_input_3').label =
              this.parityCategory === ParityType.TWOTOFOUR ? `${label} >= 5` : `${label} 3`;
          }
        }
      }
    } else {
      this.displayedTableColumns = this.displayedTableColumns
        .filter((column) => column.id.indexOf('dosator') === -1)
        .filter((column) => column.id.indexOf('feedingTimesPerDay') === -1);
    }
  }

  /**
   * Creates the advice object based on reference feed advice (if any, otherwise an empty one).
   * Also sets the properties for the feed advice.
   */
  private createInitialAdvice(
    selectedFeedAdvice: FeedAdvice,
    user: AgrUser,
    country: string,
  ): void {
    this.energySetting$
      .pipe(
        withLatestFrom(
          this.lysineSetting$,
          this.selectedFeedAdviceType$,
          this.store.select(getSelectedLocations),
        ),
        take(1),
      )
      .subscribe(([energySystem, lysineSystem, type, locationIds]) => {
        const createInitialSowFeedAdviceLines = (): SowFeedAdviceLine[] => {
          let lines = <SowFeedAdviceLine[]>[];
          let numberOfinitialLines = 0;

          switch (this.feedAdviceType) {
            case FeedAdviceType.SOWS_BREEDTOFARROW:
              numberOfinitialLines = 4;
              break;

            case FeedAdviceType.SOWS_FARROWTOWEAN:
              numberOfinitialLines = 9;
              break;

            case FeedAdviceType.SOWS_WEANTOBREED:
              numberOfinitialLines = 2;
              break;

            default:
              break;
          }

          // Parity Registrations
          const parityRegistrations = <ParityRegistration[]>[];
          const parity = selectedFeedAdvice ? selectedFeedAdvice.parity : ParityType.ONETOTWO;
          const parityNumber = parity === ParityType.ONETOTWO ? 2 : 5;
          for (let parity = 1; parity <= parityNumber; parity++) {
            parityRegistrations.push(<ParityRegistration>{
              parity,
              feedKg: null,
              ewKg: null,
              lysKg: null,
            });
          }

          if (this.feedAdviceType === FeedAdviceType.SOWS_FARROWTOWEAN) {
            lines = SowsFeedAdviceDetailComponent.getInitialLinesFarrowToWean(parityRegistrations);
          } else if (this.feedAdviceType === FeedAdviceType.SOWS_WEANTOBREED) {
            lines = SowsFeedAdviceDetailComponent.getInitialLinesWeanToBreed(parityRegistrations);
          } else {
            for (let line = 1; line <= numberOfinitialLines; line++) {
              lines.push(<SowFeedAdviceLine>{
                id: line - 1,
                dayFrom: line === 1 ? 1 : null,
                dayTo: null,
                parityRegistrations,
                product: <Product>{},
              });
            }
          }

          return lines;
        };

        this.advice = {
          id: this.mode === 'create' ? 0 : selectedFeedAdvice.id,
          name: selectedFeedAdvice ? selectedFeedAdvice.name : '',
          isReferenceFeedAdvice: this.isReferenceFeedAdvice,
          description: selectedFeedAdvice ? selectedFeedAdvice.description : '',
          usedReferenceFeedAdviceId: selectedFeedAdvice ? selectedFeedAdvice.id : null,
          locationIds: selectedFeedAdvice ? selectedFeedAdvice.locationIds : locationIds,
          advisorName: user.name,
          type: type
            ? type
            : selectedFeedAdvice
              ? selectedFeedAdvice.type
              : FeedAdviceType.SOWS_BREEDTOFARROW,
          feedAdviceType: type
            ? type
            : selectedFeedAdvice
              ? <FeedAdviceType>selectedFeedAdvice.type
              : FeedAdviceType.SOWS_BREEDTOFARROW,
          updated: selectedFeedAdvice ? selectedFeedAdvice.updated : null,
          advisorProfileId: selectedFeedAdvice ? selectedFeedAdvice.advisorProfileId : null,
          customerId: selectedFeedAdvice ? selectedFeedAdvice.customerId : null,
          energySystem,
          lysineSystem,
          country: this.isReferenceFeedAdvice ? country : null,
          phases: [],
          fatteningRearingFeedAdviceLines: [],
          geneticLine: selectedFeedAdvice ? selectedFeedAdvice.geneticLine : null,
          parity: selectedFeedAdvice ? selectedFeedAdvice.parity : ParityType.ONETOTWO,
          useParitySystem: selectedFeedAdvice ? selectedFeedAdvice.useParitySystem : true,
          parityAlternative2: selectedFeedAdvice
            ? selectedFeedAdvice.parityAlternative2
            : undefined,
          parityAlternative3: selectedFeedAdvice
            ? selectedFeedAdvice.parityAlternative3
            : undefined,
          parityAlternative4: selectedFeedAdvice
            ? selectedFeedAdvice.parityAlternative4
            : undefined,
          parityAlternative5: selectedFeedAdvice
            ? selectedFeedAdvice.parityAlternative5
            : undefined,
          parityAlternative6: selectedFeedAdvice
            ? selectedFeedAdvice.parityAlternative6
            : undefined,
          inputSystem: selectedFeedAdvice
            ? selectedFeedAdvice.inputSystem
            : InputSystemType.ENERGYDAY,
          isSow: true,
          bodyCondition: selectedFeedAdvice ? selectedFeedAdvice.bodyCondition : null,
          sowFeedAdviceLines: selectedFeedAdvice
            ? selectedFeedAdvice.sowFeedAdviceLines
            : createInitialSowFeedAdviceLines(),
          pigletsPerFarrowingCategory: selectedFeedAdvice
            ? selectedFeedAdvice.pigletsPerFarrowingCategory
            : null,
          transitionDay: selectedFeedAdvice
            ? selectedFeedAdvice.transitionDay
            : <FeedAdviceType>type === FeedAdviceType.SOWS_FARROWTOWEAN
              ? 111
              : null,
          weanedPigletFeedAdviceLines: [],
          showDosatorColumns: selectedFeedAdvice?.showDosatorColumns ?? false,
        };

        this.parityCategory = this.advice.parity;
        this.inputSystem = this.advice.inputSystem;
        this.showDosatorColumns = this.advice.showDosatorColumns;
        this.feedAdviceProperties = <FeedAdviceProperties>{
          feedAdviceType: this.advice.feedAdviceType,
          parityCategory: this.advice.parity,
          geneticLine: this.advice.geneticLine,
          bodyCondition: this.advice.bodyCondition,
          inputSystem: this.advice.inputSystem,
          pigletsPerFarrowingCategory: this.advice.pigletsPerFarrowingCategory,
          transitionDay: this.advice.transitionDay,
        };

        this.changeDosatorSettingSelection(selectedFeedAdvice?.selectedDosatorSettingsId);
      });
  }

  private static getInitialLinesFarrowToWean(
    parityRegistrations: ParityRegistration[],
  ): SowFeedAdviceLine[] {
    return [
      <SowFeedAdviceLine>{
        id: 0,
        dayFrom: 111,
        dayTo: 113,
        parityRegistrations,
        product: <Product>{},
      },
      <SowFeedAdviceLine>{
        id: 1,
        dayFrom: 114,
        dayTo: 115,
        parityRegistrations,
        product: <Product>{},
      },
      <SowFeedAdviceLine>{ id: 2, dayFrom: 1, dayTo: 1, parityRegistrations, product: <Product>{} },
      <SowFeedAdviceLine>{ id: 3, dayFrom: 2, dayTo: 2, parityRegistrations, product: <Product>{} },
      <SowFeedAdviceLine>{ id: 4, dayFrom: 3, dayTo: 3, parityRegistrations, product: <Product>{} },
      <SowFeedAdviceLine>{ id: 5, dayFrom: 4, dayTo: 4, parityRegistrations, product: <Product>{} },
      <SowFeedAdviceLine>{ id: 6, dayFrom: 5, dayTo: 5, parityRegistrations, product: <Product>{} },
      <SowFeedAdviceLine>{ id: 7, dayFrom: 6, dayTo: 6, parityRegistrations, product: <Product>{} },
      <SowFeedAdviceLine>{ id: 8, dayFrom: 7, dayTo: 7, parityRegistrations, product: <Product>{} },
      <SowFeedAdviceLine>{
        id: 9,
        dayFrom: 8,
        dayTo: 28,
        parityRegistrations,
        product: <Product>{},
      },
    ];
  }

  private static getInitialLinesWeanToBreed(
    parityRegistrations: ParityRegistration[],
  ): SowFeedAdviceLine[] {
    return [
      <SowFeedAdviceLine>{ id: 0, dayFrom: 1, dayTo: 3, parityRegistrations, product: <Product>{} },
      <SowFeedAdviceLine>{ id: 1, dayFrom: 4, dayTo: 7, parityRegistrations, product: <Product>{} },
    ];
  }

  /**
   * update the properties in the advice object if they are changed in the properties component
   */
  changeFeedAdviceProperties(emitted: { locations: number[]; properties: FeedAdviceProperties }) {
    this.advice = {
      ...this.advice,
      locationIds: emitted.locations,
      geneticLine: emitted.properties.geneticLine,
      bodyCondition: emitted.properties.bodyCondition,
      pigletsPerFarrowingCategory: emitted.properties.pigletsPerFarrowingCategory,
      transitionDay: emitted.properties.transitionDay,
    };
    this.transition.day = emitted.properties.transitionDay;
    if (emitted.properties.transitionDay) {
      this.dayFromArray.controls[0]?.updateValueAndValidity();
      this.dayFromArray.controls[0]?.markAsDirty();
      this.dayFromArray.controls[0]?.markAllAsTouched();
    }
  }

  setParitySystem(updateInputSystem = true) {
    if (this.useParitySystem && this.parityCategory === 'OneToFour') {
      this.parityCategory = ParityType.ONETOFIVE;
    }

    this.advice.useParitySystem = this.useParitySystem;

    let calculation;
    switch (this.feedAdviceType) {
      case FeedAdviceType.SOWS_BREEDTOFARROW:
        calculation = this.locale.translate('settings.energy.short.E-Dracht');
        break;
      case FeedAdviceType.SOWS_FARROWTOWEAN:
        calculation = this.locale.translate('settings.energy.short.E-Lacto');
        break;
      case FeedAdviceType.SOWS_WEANTOBREED:
        calculation = this.locale.translate('settings.energy.short.E-Lacto');
        break;
      default:
        calculation = this.locale.translate('settings.energy.short.' + this.energySystem);
    }

    this.tableColumns.find((tableColumn) => tableColumn.id === 'energyday_input_1').label =
      `${this.locale.translate('sows-feed-advice.table.energy')}
         (${calculation}/d) ${
           this.useParitySystem
             ? this.locale.translate('parity') + '1'
             : (this.advice.parityAlternative2 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'energyday_input_2').label =
      `${this.locale.translate('sows-feed-advice.table.energy')}
         (${calculation}/d) ${
           this.useParitySystem
             ? this.locale.translate('parity') + '2'
             : (this.advice.parityAlternative3 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'energyday_input_3').label =
      `${this.locale.translate('sows-feed-advice.table.energy')}
         (${calculation}/d) ${
           this.useParitySystem
             ? this.locale.translate('parity') + '3'
             : (this.advice.parityAlternative4 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'energyday_input_4').label =
      `${this.locale.translate('sows-feed-advice.table.energy')}
         (${calculation}/d) ${
           this.useParitySystem
             ? this.locale.translate('parity') + '4'
             : (this.advice.parityAlternative5 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'energyday_input_5').label =
      `${this.locale.translate('sows-feed-advice.table.energy')}
         (${calculation}/d) ${
           this.useParitySystem
             ? this.locale.translate('parity') + '5'
             : (this.advice.parityAlternative6 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'energyday_readonly_1').label =
      `${this.locale.translate('sows-feed-advice.table.energy')}
         (${calculation}/d) ${
           this.useParitySystem
             ? this.locale.translate('parity') + '1'
             : (this.advice.parityAlternative2 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'energyday_readonly_2').label =
      `${this.locale.translate('sows-feed-advice.table.energy')}
         (${calculation}/d) ${
           this.useParitySystem
             ? this.locale.translate('parity') + '2'
             : (this.advice.parityAlternative3 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'energyday_readonly_3').label =
      `${this.locale.translate('sows-feed-advice.table.energy')}
         (${calculation}/d) ${
           this.useParitySystem
             ? this.locale.translate('parity') + '3'
             : (this.advice.parityAlternative4 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'energyday_readonly_4').label =
      `${this.locale.translate('sows-feed-advice.table.energy')}
         (${calculation}/d) ${
           this.useParitySystem
             ? this.locale.translate('parity') + '4'
             : (this.advice.parityAlternative5 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'energyday_readonly_5').label =
      `${this.locale.translate('sows-feed-advice.table.energy')}
         (${calculation}/d) ${
           this.useParitySystem
             ? this.locale.translate('parity') + '5'
             : (this.advice.parityAlternative6 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'kgday_input_1').label =
      `${this.locale.translate('sows-feed-advice.table.feed')}
         (kg/d) ${
           this.useParitySystem
             ? this.locale.translate('parity') + '1'
             : this.advice.parityAlternative2
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'kgday_input_2').label =
      `${this.locale.translate('sows-feed-advice.table.feed')}
    (kg/d) ${
      this.useParitySystem
        ? this.locale.translate('parity') + '2'
        : (this.advice.parityAlternative3 ?? '')
    } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'kgday_input_3').label =
      `${this.locale.translate('sows-feed-advice.table.feed')}
    (kg/d) ${
      this.useParitySystem
        ? this.locale.translate('parity') + '3'
        : (this.advice.parityAlternative4 ?? '')
    } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'kgday_input_4').label =
      `${this.locale.translate('sows-feed-advice.table.feed')}
    (kg/d) ${
      this.useParitySystem
        ? this.locale.translate('parity') + '4'
        : (this.advice.parityAlternative5 ?? '')
    } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'kgday_input_5').label =
      `${this.locale.translate('sows-feed-advice.table.feed')}
    (kg/d) ${
      this.useParitySystem
        ? this.locale.translate('parity') + '5'
        : (this.advice.parityAlternative6 ?? '')
    } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'kgday_readonly_1').label =
      `${this.locale.translate('sows-feed-advice.table.feed')}
    (kg/d) ${
      this.useParitySystem
        ? this.locale.translate('parity') + '1'
        : (this.advice.parityAlternative2 ?? '')
    } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'kgday_readonly_2').label =
      `${this.locale.translate('sows-feed-advice.table.feed')}
    (kg/d) ${
      this.useParitySystem
        ? this.locale.translate('parity') + '2'
        : (this.advice.parityAlternative3 ?? '')
    } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'kgday_readonly_3').label =
      `${this.locale.translate('sows-feed-advice.table.feed')}
    (kg/d) ${
      this.useParitySystem
        ? this.locale.translate('parity') + '3'
        : (this.advice.parityAlternative4 ?? '')
    } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'kgday_readonly_4').label =
      `${this.locale.translate('sows-feed-advice.table.feed')}
    (kg/d) ${
      this.useParitySystem
        ? this.locale.translate('parity') + '4'
        : (this.advice.parityAlternative5 ?? '')
    } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'kgday_readonly_5').label =
      `${this.locale.translate('sows-feed-advice.table.feed')}
    (kg/d) ${
      this.useParitySystem
        ? this.locale.translate('parity') + '5'
        : (this.advice.parityAlternative6 ?? '')
    } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'lysine_readonly_1').label =
      `${this.locale.translate('sows-feed-advice.table.lysine')}
    (${this.lysineSystem}/d) ${
      this.useParitySystem
        ? this.locale.translate('parity') + '1'
        : (this.advice.parityAlternative2 ?? '')
    } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'lysine_readonly_2').label =
      `${this.locale.translate('sows-feed-advice.table.lysine')}
    (${this.lysineSystem}/d) ${
      this.useParitySystem
        ? this.locale.translate('parity') + '2'
        : (this.advice.parityAlternative3 ?? '')
    } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'lysine_readonly_3').label =
      `${this.locale.translate('sows-feed-advice.table.lysine')}
    (${this.lysineSystem}/d) ${
      this.useParitySystem
        ? this.locale.translate('parity') + '3'
        : (this.advice.parityAlternative4 ?? '')
    } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'lysine_readonly_4').label =
      `${this.locale.translate('sows-feed-advice.table.lysine')}
    (${this.lysineSystem}/d) ${
      this.useParitySystem
        ? this.locale.translate('parity') + '4'
        : (this.advice.parityAlternative5 ?? '')
    } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'lysine_readonly_5').label =
      `${this.locale.translate('sows-feed-advice.table.lysine')}
    (${this.lysineSystem}/d) ${
      this.useParitySystem
        ? this.locale.translate('parity') + '5'
        : (this.advice.parityAlternative6 ?? '')
    } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'dosator_input_1').label =
      `${this.locale.translate('sows-feed-advice.table.dosator')}
         ${
           this.useParitySystem
             ? this.locale.translate('parity') + '1'
             : (this.advice.parityAlternative2 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'dosator_input_2').label =
      `${this.locale.translate('sows-feed-advice.table.dosator')}
         ${
           this.useParitySystem
             ? this.locale.translate('parity') + '2'
             : (this.advice.parityAlternative3 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'dosator_input_3').label =
      `${this.locale.translate('sows-feed-advice.table.dosator')}
          ${
            this.useParitySystem
              ? this.locale.translate('parity') + '3'
              : (this.advice.parityAlternative4 ?? '')
          } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'dosator_input_4').label =
      `${this.locale.translate('sows-feed-advice.table.dosator')}
         ${
           this.useParitySystem
             ? this.locale.translate('parity') + '4'
             : (this.advice.parityAlternative5 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'dosator_input_5').label =
      `${this.locale.translate('sows-feed-advice.table.dosator')}
         ${
           this.useParitySystem
             ? this.locale.translate('parity') + '5'
             : (this.advice.parityAlternative6 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'dosator_readonly_input_1').label =
      `${this.locale.translate('sows-feed-advice.table.dosator')}
         ${
           this.useParitySystem
             ? this.locale.translate('parity') + '1'
             : (this.advice.parityAlternative2 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'dosator_readonly_input_2').label =
      `${this.locale.translate('sows-feed-advice.table.dosator')}
         ${
           this.useParitySystem
             ? this.locale.translate('parity') + '2'
             : (this.advice.parityAlternative3 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'dosator_readonly_input_3').label =
      `${this.locale.translate('sows-feed-advice.table.dosator')}
         ${
           this.useParitySystem
             ? this.locale.translate('parity') + '3'
             : (this.advice.parityAlternative4 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'dosator_readonly_input_4').label =
      `${this.locale.translate('sows-feed-advice.table.dosator')}
         ${
           this.useParitySystem
             ? this.locale.translate('parity') + '4'
             : (this.advice.parityAlternative5 ?? '')
         } `;

    this.tableColumns.find((tableColumn) => tableColumn.id === 'dosator_readonly_input_5').label =
      `${this.locale.translate('sows-feed-advice.table.dosator')}
        ${
          this.useParitySystem
            ? this.locale.translate('parity') + '5'
            : (this.advice.parityAlternative6 ?? '')
        } `;

    if (updateInputSystem) {
      this.setParityAndInputSystem();
    }
  }

  /**
   * if Parity or DataCaptureSetting is changed, the table columns must be recalculated.
   */
  setParityAndInputSystem() {
    const getValueFromDatasource = (
      row: SowFeedadviceTableRow,
      parity: number,
      type: string,
    ): number => {
      for (const [key, value] of Object.entries(row)) {
        if (key.startsWith(type) && key.endsWith(String(parity))) {
          return value;
        }
      }
    };

    if (this.inputSystem === InputSystemType.DOSATOR && !this.showDosatorColumns) {
      this.showDosatorColumns = true;
    }

    // When a feedadvice is based on a reference advice and user switches parity setting,
    // then the FormArrays are not all automatically populated with the data from datasource.
    this.tableConfig.dataSource.data.forEach((row: SowFeedadviceTableRow, index) => {
      if (this.productArray.controls[index].value === null) {
        this.productArray.controls[index].setValue(row.product);
      }
      if (this.feedingTimesPerDayArray.controls[index].value === null) {
        this.feedingTimesPerDayArray.controls[index].setValue(row.feedingTimesPerDay);
      }
      for (let parity = 1; parity <= 5; parity++) {
        if ((<FormArray>this.energyArrays[parity - 1]).controls[index].value === null) {
          (<FormArray>this.energyArrays[parity - 1]).controls[index].setValue(
            getValueFromDatasource(row, parity, 'energyday'),
          );
        }
        if ((<FormArray>this.feedArrays[parity - 1]).controls[index].value === null) {
          (<FormArray>this.feedArrays[parity - 1]).controls[index].setValue(
            getValueFromDatasource(row, parity, 'kgday'),
          );
        }
        if ((<FormArray>this.lysineArrays[parity - 1]).controls[index].value === null) {
          (<FormArray>this.lysineArrays[parity - 1]).controls[index].setValue(
            getValueFromDatasource(row, parity, 'lysine'),
          );
        }
        if ((<FormArray>this.dosatorArrays[parity - 1]).controls[index].value === null) {
          (<FormArray>this.dosatorArrays[parity - 1]).controls[index].setValue(
            getValueFromDatasource(row, parity, 'dosator'),
          );
        }
      }
    });
    this.updateDataSource();
    if (this.mode !== 'view') {
      this.updateSummary();
    }

    this.getDisplayedTableColumns();

    // save ShowLysineSetting in settings service
    this.store.dispatch(
      actions.setShowLysineSetting({ save: true, showLysine: this.showLysineColumns }),
    );

    this.store.dispatch(
      actions.setShowEnergySetting({ save: true, showEnergy: this.showEnergyColumns }),
    );

    this.store.dispatch(
      actions.setShowDosatorSetting({ save: true, showDosator: this.showDosatorColumns }),
    );

    this.tableConfig = <TableConfig>{
      ...this.tableConfig,
      columns: this.displayedTableColumns,
    };
  }

  getParityHeaderText(parity: number): string {
    switch (this.parityCategory) {
      case ParityType.ONETOTWO:
        return parity === 2 ? `>= ${parity}` : `${parity}`;
      case ParityType.TWOTOFOUR:
        if (parity === 1) {
          return '1';
        } else if (parity === 2) {
          return '2-4';
        } else if (parity === 3) {
          return '>= 5';
        }
        break;
      case ParityType.ONETOFIVE:
        return parity === 5 ? `>= ${parity}` : `${parity}`;
    }
  }

  private getParityNumber(): number {
    switch (this.parityCategory) {
      case ParityType.ONETOTWO:
        return 2;
      case ParityType.ONETOFOUR:
        return 4;
      case ParityType.TWOTOFOUR:
        return 3;
      case ParityType.ONETOFIVE:
        return 5;
    }
  }

  getCalculateButtonLabel(): string {
    return this.inputSystem === InputSystemType.ENERGYDAY
      ? this.locale.translate('sows-feed-advice.buttons.calculate-feed')
      : this.locale.translate('sows-feed-advice.buttons.calculate-columns');
  }

  /**
   * If an energy field is edited and a product is selected, then the feed field and dosator field must be calculated.
   * (vice versa if the feed field or a dosator field is edited )
   */
  updateFeedadviceRows() {
    this.tableConfig.dataSource.data.forEach((_, index) => {
      const product = this.productArray.value[index] as Product;
      if (!!product && !this.isEmptyObject(product)) {
        const parityNumber = this.getParityNumber();
        for (let parity = 1; parity <= parityNumber; parity++) {
          switch (this.inputSystem) {
            case InputSystemType.ENERGYDAY: {
              const energyValue = (<FormArray>this.energyArrays[parity - 1]).controls[index].value;
              (<FormArray>this.feedArrays[parity - 1]).controls[index].setValue(
                energyValue / product.ewKg,
              );
              (<FormArray>this.dosatorArrays[parity - 1]).controls[index].setValue(
                this.calculateDosator(product, energyValue / product.ewKg),
              );
              break;
            }
            case InputSystemType.KGDAY: {
              let feedValue = (<FormArray>this.feedArrays[parity - 1]).controls[index].value;

              if (this.feedingTimesPerDayArray.controls[index].value) {
                const feedingTimesPerDay = this.feedingTimesPerDayArray.controls[index].value;
                feedValue = feedValue / feedingTimesPerDay;
              }

              (<FormArray>this.energyArrays[parity - 1]).controls[index].setValue(
                feedValue * product.ewKg,
              );
              (<FormArray>this.dosatorArrays[parity - 1]).controls[index].setValue(
                this.calculateDosator(product, feedValue),
              );
              break;
            }
            case InputSystemType.DOSATOR: {
              const dosatorValue = (<FormArray>this.dosatorArrays[parity - 1]).controls[index]
                .value;
              let feedValueBasedOnDosator = this.calculateFeedBasedonDosator(product, dosatorValue);

              if (this.feedingTimesPerDayArray.controls[index].value) {
                const feedingTimesPerDay = this.feedingTimesPerDayArray.controls[index].value;
                feedValueBasedOnDosator = feedValueBasedOnDosator * feedingTimesPerDay;
              }

              (<FormArray>this.feedArrays[parity - 1]).controls[index].setValue(
                feedValueBasedOnDosator,
              );
              (<FormArray>this.energyArrays[parity - 1]).controls[index].setValue(
                feedValueBasedOnDosator * product.ewKg,
              );
              break;
            }
          }
          const feedValue = (<FormArray>this.feedArrays[parity - 1]).controls[index].value;
          (<FormArray>this.lysineArrays[parity - 1]).controls[index].setValue(
            feedValue * product.lysKg,
          );
        }
        this.updateDataSource();
        this.updateSummary();
      }
    });
  }

  calculateDosator(product: Product, kilogram: number) {
    let liter: number;

    if (kilogram > 0) {
      const dosatorProduct = this.selectedDosatorSetting?.products.find(
        (dosatorProduct: DosatorProduct) => dosatorProduct.productId === product.id,
      );

      if (dosatorProduct) {
        liter = dosatorProduct.conversions.find(
          (conversion) => conversion.kilogram === kilogram,
        )?.liter;

        if (!liter) {
          let lowerValueKilogram: number;
          let higherValueKilogram: number;
          let lowerValueLiter: number;
          let higherValueLiter: number;
          for (let i = 0, len = dosatorProduct.conversions.length; i < len; i++) {
            if (kilogram > dosatorProduct.conversions[i].kilogram) {
              lowerValueKilogram = dosatorProduct.conversions[i].kilogram;
              lowerValueLiter = dosatorProduct.conversions[i].liter;
            }
            if (kilogram < dosatorProduct.conversions[i].kilogram) {
              higherValueKilogram = dosatorProduct.conversions[i].kilogram;
              higherValueLiter = dosatorProduct.conversions[i].liter;
            }

            if (higherValueKilogram) {
              break;
            }
          }
          if (lowerValueLiter && higherValueLiter) {
            liter =
              ((kilogram - lowerValueKilogram) / (higherValueKilogram - lowerValueKilogram)) *
                (higherValueLiter - lowerValueLiter) +
              lowerValueLiter;
          } else if (!lowerValueLiter) {
            const conversion = dosatorProduct.conversions[0];
            liter = kilogram / conversion.kilogramPerLiter;
          } else if (!higherValueLiter) {
            const len = dosatorProduct.conversions.length;
            const conversion = dosatorProduct.conversions[len - 1];
            liter = kilogram / conversion.kilogramPerLiter;
          }
        }
      }
    }
    return liter;
  }

  calculateFeedBasedonDosator(product: Product, liter: number) {
    let kilogram: number;

    if (liter > 0) {
      const dosatorProduct = this.selectedDosatorSetting?.products.find(
        (dosatorProduct: DosatorProduct) => dosatorProduct.productId === product.id,
      );

      if (dosatorProduct) {
        kilogram = dosatorProduct.conversions.find(
          (conversion) => conversion.liter === liter,
        )?.kilogram;

        if (!kilogram) {
          let lowerValueKilogram: number;
          let higherValueKilogram: number;
          let lowerValueLiter: number;
          let higherValueLiter: number;
          for (let i = 0, len = dosatorProduct.conversions.length; i < len; i++) {
            if (liter > dosatorProduct.conversions[i].liter) {
              lowerValueKilogram = dosatorProduct.conversions[i].kilogram;
              lowerValueLiter = dosatorProduct.conversions[i].liter;
            }
            if (liter < dosatorProduct.conversions[i].liter) {
              higherValueKilogram = dosatorProduct.conversions[i].kilogram;
              higherValueLiter = dosatorProduct.conversions[i].liter;
            }

            if (higherValueKilogram) {
              break;
            }
          }

          if (lowerValueKilogram && higherValueKilogram) {
            kilogram =
              ((liter - lowerValueLiter) / (higherValueLiter - lowerValueLiter)) *
                (higherValueKilogram - lowerValueKilogram) +
              lowerValueKilogram;
          } else if (!lowerValueKilogram) {
            const conversion = dosatorProduct.conversions[0];
            kilogram = liter * conversion.kilogramPerLiter;
          } else if (!higherValueKilogram) {
            const len = dosatorProduct.conversions.length;
            const conversion = dosatorProduct.conversions[len - 1];
            kilogram = liter * conversion.kilogramPerLiter;
          }
        }
      }
    }

    return kilogram;
  }

  // helper function to set selected product in the table row.
  // We need to use a compareWith function because it does not work automatically in this case.
  areProductsEqual(p1: Product, p2: Product): boolean {
    if (!p1 || !p2) {
      return false;
    }
    return p1.id === p2.id && p1.code === p2.code;
  }

  // returns the selected product
  getProduct(row: SowFeedadviceTableRow, products: Product[]): Product {
    let foundProduct = <Product>{};
    if (row.product) {
      foundProduct = !!products && products.find((product) => product.code === row.product.code);
    }
    return foundProduct;
  }

  /**
   * Sets a selected product in the FormArray.
   * Then calculates the feed or energy value in that row.
   */
  selectProduct(selection: MatSelectChange, tableRow: SowFeedadviceTableRow) {
    this.productArray.controls[tableRow.id].setValue(<Product>selection.value);
    this.addProductToLowerRows(selection, tableRow);
    this.updateDataSource();
    this.updateSummary();
  }

  // Select a dosator settings
  changeDosatorSettingSelection(dosatorSettingId?: number) {
    this.dosatorSettings$.pipe(take(1)).subscribe((dosatorSettings) => {
      const foundDosatorSetting = dosatorSettings.find(
        (dosatorSetting) => dosatorSetting.id === Number(dosatorSettingId),
      );
      if (foundDosatorSetting) {
        this.selectedDosatorSetting = foundDosatorSetting;
      } else if (dosatorSettings.length > 0) {
        this.selectedDosatorSetting = dosatorSettings[0];
      } else {
        this.selectedDosatorSetting = null;
      }
    });
  }

  // Sets the selected product for all rows below the parameters.
  addProductToLowerRows(selection: MatSelectChange, tableRow: SowFeedadviceTableRow) {
    const tableData = this.tableConfig.dataSource.filteredData.slice();
    const rowIndex = tableData.findIndex((d: SowFeedadviceTableRow) => d.id === tableRow.id);
    tableData.forEach((d: SowFeedadviceTableRow, i: number) => {
      if (i > rowIndex) {
        this.productArray.controls[d.id].setValue(<Product>selection.value);
      }
    });
  }

  // put a click on add/remove row to top/bottom to a clickstream
  changeNumberOfTableRows(operation: string) {
    this.changeNumberOfTableRowsClickStream$.next(operation);
  }

  onFilterProductsKeyUp(value: string) {
    this.filterProducts(value);
  }

  onProductOpenedChange() {
    this.resetFilterProducts();
  }

  resetFilterProducts() {
    this.filterProducts();
    this.filterProductsValue = '';
  }

  filterProducts(value?: string) {
    return this.products$
      .pipe(
        skipWhile((p) => p === undefined),
        take(1),
      )
      .subscribe((products) => {
        if (value) {
          value = value.toLocaleLowerCase();
          this.filteredProducts = products.filter(
            (p) =>
              p.name.toLocaleLowerCase().startsWith(value) ||
              p.code.toLocaleLowerCase().startsWith(value),
          );

          this.productArray.controls.forEach((productControl) => {
            if (
              productControl.value?.code &&
              !this.filteredProducts.find((p) => p.code === productControl.value.code)
            ) {
              this.filteredProducts.push(productControl.value);
            }
          });

          if (!this.filteredProducts.length) {
            this.filteredProducts = products;
          }
        } else {
          this.filteredProducts = products;
        }
      });
  }

  /**
   * Runs when a click on add/remove row to top/bottom (from clickstream in constructor)
   */
  recalculateNumberOfTableRows(operation: string) {
    const renumberRowIds = (tablerows: SowFeedadviceTableRow[]): SowFeedadviceTableRow[] =>
      tablerows.map(
        (row, index) =>
          <SowFeedadviceTableRow>{
            ...row,
            id: index,
          },
      );
    let index: number;
    this.updateDataSource();
    let tableRows = this.tableConfig.dataSource.data.map((row: SowFeedadviceTableRow) =>
      Object.assign({}, row),
    );

    switch (operation) {
      case 'add-top': {
        index = Math.min(...tableRows.map((row) => row.id), 0) - 1;
        tableRows = renumberRowIds([
          <SowFeedadviceTableRow>{
            id: index,
            dayFrom:
              this.feedAdviceType === FeedAdviceType.SOWS_FARROWTOWEAN
                ? this.advice?.transitionDay
                : 1,
            dayTo: null,
            product: {},
          },
          ...tableRows,
        ]);
        index = index < 0 ? 0 : index;
        this.dayFromArray.insert(index, new FormControl());
        this.dayToArray.insert(index, new FormControl());
        this.productArray.insert(index, new FormControl());
        this.feedingTimesPerDayArray.insert(index, new FormControl());
        this.energyArrays.forEach((array: FormArray) => array.insert(index, new FormControl()));
        this.feedArrays.forEach((array: FormArray) => array.insert(index, new FormControl()));
        this.lysineArrays.forEach((array: FormArray) => array.insert(index, new FormControl()));
        this.dosatorArrays.forEach((array: FormArray) => array.insert(index, new FormControl()));
        break;
      }
      case 'remove-top': {
        tableRows = renumberRowIds(tableRows.filter((row) => row.id !== tableRows[0].id));
        this.dayFromArray.removeAt(0);
        this.dayToArray.removeAt(0);
        this.productArray.removeAt(0);
        this.feedingTimesPerDayArray.removeAt(0);
        this.energyArrays.forEach((array: FormArray) => array.removeAt(0));
        this.feedArrays.forEach((array: FormArray) => array.removeAt(0));
        this.lysineArrays.forEach((array: FormArray) => array.removeAt(0));
        this.dosatorArrays.forEach((array: FormArray) => array.removeAt(0));
        break;
      }
      case 'add-bottom': {
        index = Math.max(...tableRows.map((row) => row.id), 0) + 1;
        tableRows = [
          ...tableRows,
          <SowFeedadviceTableRow>{
            id: index,
            dayFrom: null,
            dayTo: null,
            product: {},
          },
        ];
        this.dayFromArray.insert(index, new FormControl());
        this.dayToArray.insert(index, new FormControl());
        this.productArray.insert(index, new FormControl());
        this.feedingTimesPerDayArray.insert(index, new FormControl());
        this.energyArrays.forEach((array: FormArray) => array.insert(index, new FormControl()));
        this.feedArrays.forEach((array: FormArray) => array.insert(index, new FormControl()));
        this.lysineArrays.forEach((array: FormArray) => array.insert(index, new FormControl()));
        this.dosatorArrays.forEach((array: FormArray) => array.insert(index, new FormControl()));
        break;
      }
      case 'remove-bottom': {
        const lastIndex = tableRows.length - 1;
        tableRows = tableRows.filter((row) => row.id !== lastIndex);
        this.dayFromArray.removeAt(lastIndex);
        this.dayToArray.removeAt(lastIndex);
        this.productArray.removeAt(lastIndex);
        this.feedingTimesPerDayArray.removeAt(lastIndex);
        this.energyArrays.forEach((array: FormArray) => array.removeAt(lastIndex));
        this.feedArrays.forEach((array: FormArray) => array.removeAt(lastIndex));
        this.lysineArrays.forEach((array: FormArray) => array.removeAt(lastIndex));
        this.dosatorArrays.forEach((array: FormArray) => array.removeAt(lastIndex));
        break;
      }
    }
    this.tableConfig.dataSource.data = tableRows;
    this.updateSummary();
    this.dayFromArray.controls.forEach((ctrl) => {
      ctrl.markAsDirty();
      ctrl.markAllAsTouched();
    });
    this.dayToArray.controls.forEach((ctrl) => {
      ctrl.markAsDirty();
      ctrl.markAllAsTouched();
    });
  }

  /**
   * updates feedadvice with all data from the formcontrols and updates datasource in the table
   */
  private updateDataSource() {
    const tableRows = this.tableConfig.dataSource.data.map((row: SowFeedadviceTableRow) =>
      Object.assign({}, row),
    );
    tableRows.forEach((row) => {
      row.dayFrom = <number>this.dayFromArray.value[row.id];
      row.dayTo = <number>this.dayToArray.value[row.id];
      row.energyday_input_1 = <number>(<FormArray>this.energyArrays[0]).value[row.id];
      row.energyday_input_2 = <number>(<FormArray>this.energyArrays[1]).value[row.id];
      row.energyday_input_3 = <number>(<FormArray>this.energyArrays[2]).value[row.id];
      row.energyday_input_4 = <number>(<FormArray>this.energyArrays[3]).value[row.id];
      row.energyday_input_5 = <number>(<FormArray>this.energyArrays[4]).value[row.id];
      row.energyday_readonly_1 = <number>(<FormArray>this.energyArrays[0]).value[row.id];
      row.energyday_readonly_2 = <number>(<FormArray>this.energyArrays[1]).value[row.id];
      row.energyday_readonly_3 = <number>(<FormArray>this.energyArrays[2]).value[row.id];
      row.energyday_readonly_4 = <number>(<FormArray>this.energyArrays[3]).value[row.id];
      row.energyday_readonly_5 = <number>(<FormArray>this.energyArrays[4]).value[row.id];
      row.product = this.productArray.value[row.id] as Product;
      row.kgday_input_1 = <number>(<FormArray>this.feedArrays[0]).value[row.id];
      row.kgday_input_2 = <number>(<FormArray>this.feedArrays[1]).value[row.id];
      row.kgday_input_3 = <number>(<FormArray>this.feedArrays[2]).value[row.id];
      row.kgday_input_4 = <number>(<FormArray>this.feedArrays[3]).value[row.id];
      row.kgday_input_5 = <number>(<FormArray>this.feedArrays[4]).value[row.id];
      row.kgday_readonly_1 = <number>(<FormArray>this.feedArrays[0]).value[row.id];
      row.kgday_readonly_2 = <number>(<FormArray>this.feedArrays[1]).value[row.id];
      row.kgday_readonly_3 = <number>(<FormArray>this.feedArrays[2]).value[row.id];
      row.kgday_readonly_4 = <number>(<FormArray>this.feedArrays[3]).value[row.id];
      row.kgday_readonly_5 = <number>(<FormArray>this.feedArrays[4]).value[row.id];
      row.lysine_readonly_1 = <number>(<FormArray>this.lysineArrays[0]).value[row.id];
      row.lysine_readonly_2 = <number>(<FormArray>this.lysineArrays[1]).value[row.id];
      row.lysine_readonly_3 = <number>(<FormArray>this.lysineArrays[2]).value[row.id];
      row.lysine_readonly_4 = <number>(<FormArray>this.lysineArrays[3]).value[row.id];
      row.lysine_readonly_5 = <number>(<FormArray>this.lysineArrays[4]).value[row.id];
      row.feedingTimesPerDay = <number>this.feedingTimesPerDayArray.value[row.id];
      row.dosator_input_1 = <number>(<FormArray>this.dosatorArrays[0]).value[row.id];
      row.dosator_input_2 = <number>(<FormArray>this.dosatorArrays[1]).value[row.id];
      row.dosator_input_3 = <number>(<FormArray>this.dosatorArrays[2]).value[row.id];
      row.dosator_input_4 = <number>(<FormArray>this.dosatorArrays[3]).value[row.id];
      row.dosator_input_5 = <number>(<FormArray>this.dosatorArrays[4]).value[row.id];
      row.dosator_readonly_input_1 = <number>(<FormArray>this.dosatorArrays[0]).value[row.id];
      row.dosator_readonly_input_2 = <number>(<FormArray>this.dosatorArrays[1]).value[row.id];
      row.dosator_readonly_input_3 = <number>(<FormArray>this.dosatorArrays[2]).value[row.id];
      row.dosator_readonly_input_4 = <number>(<FormArray>this.dosatorArrays[3]).value[row.id];
      row.dosator_readonly_input_5 = <number>(<FormArray>this.dosatorArrays[4]).value[row.id];
    });
    this.tableConfig.dataSource.data = tableRows;
  }

  /**
   * V A L I D A T I O N S
   */
  isEmptyObject = (obj: unknown): boolean =>
    obj && Object.keys(obj).length === 0 && obj.constructor === Object;

  hasErrors() {
    return (
      this.dayFromArray.controls.some((c) => c.errors !== null) ||
      this.dayToArray.controls.some((c) => c.errors !== null) ||
      !this.checkEmptyTableValues()
    );
  }
  /**
   * update feedadvice with all data from the table and then dispatches the save event.
   */

  private getSowFeedAdviceLines(): SowFeedAdviceLine[] {
    const lines: SowFeedAdviceLine[] = [];
    this.tableConfig?.dataSource?.data.forEach((tableRow: SowFeedadviceTableRow, index: number) => {
      lines.push(<SowFeedAdviceLine>{
        id: this.mode === 'create' ? index : tableRow.id,
        dayFrom:
          this.mode === 'create'
            ? ((<FormArray>this.dayFromArray).controls[index].value as number)
            : tableRow.dayFrom,
        dayTo:
          this.mode === 'create'
            ? ((<FormArray>this.dayToArray).controls[index].value as number)
            : tableRow.dayTo,
        product:
          this.mode === 'create'
            ? ((<FormArray>this.productArray).controls[index].value as Product)
            : tableRow.product,
        feedingTimesPerDay:
          this.mode === 'create'
            ? ((<FormArray>this.feedingTimesPerDayArray).controls[index].value as number)
            : tableRow.feedingTimesPerDay,
        parityRegistrations: this.getParityRegistrations(
          this.mode === 'create' ? index : tableRow.id,
        ),
      });
    });
    return lines;
  }

  private getParityRegistrations(id: number): ParityRegistration[] {
    const regs: ParityRegistration[] = [];
    const parityNumber = this.getParityNumber();
    for (let parity = 1; parity <= parityNumber; parity++) {
      const energyControls = (<FormArray>this.energyArrays[parity - 1]).controls;
      const feedControls = (<FormArray>this.feedArrays[parity - 1]).controls;
      const lysineControls = (<FormArray>this.lysineArrays[parity - 1]).controls;
      const dosatorControls = (<FormArray>this.dosatorArrays[parity - 1]).controls;
      regs.push(<ParityRegistration>{
        parity,
        feedKg: feedControls.length > 0 ? (feedControls[id].value as number) : null,
        ewKg: energyControls.length > 0 ? (energyControls[id].value as number) : null,
        lysKg: lysineControls.length > 0 ? (lysineControls[id].value as number) : null,
        dosatorPosition: dosatorControls.length > 0 ? (dosatorControls[id].value as number) : null,
      });
    }
    return regs;
  }

  private checkEmptyTableValues(): boolean {
    this.errorsDescription = '';
    const dayFrom = this.dayFromArray.getRawValue().some((val) => val === null);
    const dayTo = this.dayToArray.getRawValue().some((val) => val === null);
    const products = this.productArray.getRawValue().some((val) => this.isEmptyObject(val));
    if (dayFrom || dayTo || products) {
      this.errorsDescription += `<li>${this.locale.translate(
        'sows-feed-advice.validation-error.description.product',
      )}</li>`;
      return false;
    }
    const parityNumber = this.getParityNumber();
    for (let parity = 1; parity <= parityNumber; parity++) {
      const energy =
        this.inputSystem === InputSystemType.ENERGYDAY
          ? (<FormArray>this.energyArrays[parity - 1]).getRawValue().some((val) => val === null)
          : false;
      const feed =
        this.inputSystem === InputSystemType.KGDAY
          ? (<FormArray>this.feedArrays[parity - 1]).getRawValue().some((val) => val === null)
          : false;
      const lysine = (<FormArray>this.lysineArrays[parity - 1])
        .getRawValue()
        .some((val) => val === null);
      if (energy || feed || lysine) {
        this.errorsDescription += `<li>${this.locale.translate(
          'sows-feed-advice.validation-error.description.energy-value',
        )}</li><li>${this.locale.translate(
          'sows-feed-advice.validation-error.description.food-per-day',
        )}</li>`;
        return false;
      }
      if (this.transition?.day) {
        if (this.dayFromArray.controls[0].value !== this.transition?.day) {
          this.errorsDescription += `<li>${this.locale.translate(
            'sows-feed-advice.validation-error.description.transition-day',
          )}</li>`;

          return false;
        }
        if (this.transition?.day < 1 || this.transition?.day > 115) {
          this.errorsDescription += `<li>${this.locale.translate(
            'sows-feed-advice.validation-error.description.max-transition-day',
          )}</li>`;
          return false;
        }
      }
    }
    return true;
  }

  getCorrectEwTranslation(energySystem: string) {
    switch (this.feedAdviceType) {
      case FeedAdviceType.SOWS_BREEDTOFARROW:
        return this.locale.translate('settings.energy.short.E-Dracht');
      case FeedAdviceType.SOWS_FARROWTOWEAN:
        return this.locale.translate('settings.energy.short.E-Lacto');
      case FeedAdviceType.SOWS_WEANTOBREED:
        return this.locale.translate('settings.energy.short.E-Lacto');
      default:
        return this.locale.translate('settings.energy.short.' + energySystem);
    }
  }

  /**
   * update feedadvice with all data from the table and then dispatches the save event.
   */
  save() {
    this.updateFeedadviceRows();
    this.dayFromArray.controls.forEach((ctrl) => {
      ctrl.markAsDirty();
      ctrl.markAllAsTouched();
    });
    this.dayToArray.controls.forEach((ctrl) => {
      ctrl.markAsDirty();
      ctrl.markAllAsTouched();
    });

    if (this.validateFeedAdvice(this.advice)) {
      this.energySetting$
        .pipe(
          withLatestFrom(this.lysineSetting$),
          skipWhile(([energy, lysine]) => energy === null || lysine === null),
          take(1),
        )
        .subscribe(([energySystem, lysineSystem]) => {
          const geneticLine = Object.values(GeneticLineType)
            .map((g) => g.toString())
            .includes(this.advice.geneticLine)
            ? this.advice.geneticLine
            : null;

          this.advice = {
            ...this.advice,
            geneticLine,
            energySystem,
            lysineSystem,
            isSow: true,
            parity: this.parityCategory,
            inputSystem: this.inputSystem,
            sowFeedAdviceLines: this.getSowFeedAdviceLines(),
            showDosatorColumns: this.showDosatorColumns,
            selectedDosatorSettingsId: this.selectedDosatorSetting?.id,
          };

          this.store.dispatch(
            upsertFeedAdviceRequest({
              request: this.advice,
              isReferenceFeedAdvice: this.isReferenceFeedAdvice,
            }),
          );
        });
    }
  }

  validateFeedAdvice(feedAdvice: FeedAdvice): boolean {
    let bodyConditionIsNull = false;
    let pigletsPerFarrowingCategoryIsNull = false;

    switch (feedAdvice.feedAdviceType) {
      case FeedAdviceType.SOWS_BREEDTOFARROW:
        bodyConditionIsNull = feedAdvice.bodyCondition === null;
        break;
      case FeedAdviceType.SOWS_FARROWTOWEAN:
        pigletsPerFarrowingCategoryIsNull = feedAdvice.pigletsPerFarrowingCategory === null;
        break;
    }

    if (
      feedAdvice.name.trim().length > 0 &&
      this.checkEmptyTableValues() &&
      this.dayFromArray.controls.every((c) => c.errors === null && c.valid) &&
      this.dayToArray.controls.every((c) => c.errors === null && c.valid) &&
      !bodyConditionIsNull &&
      !pigletsPerFarrowingCategoryIsNull
    ) {
      return true;
    }

    // one or more validation errors have occurred, so the dialog message is created.
    let description = this.errorsDescription ?? '';

    if (feedAdvice.name.trim().length === 0) {
      description += `<li>${this.locale.translate(
        'sows-feed-advice.validation-error.description.feedadvice-name',
      )}</li>`;
    }

    if (bodyConditionIsNull) {
      description += `<li>${this.locale.translate(
        'sows-feed-advice.validation-error.description.body-condition',
      )}</li>`;
    }

    if (pigletsPerFarrowingCategoryIsNull) {
      description += `<li>${this.locale.translate(
        'sows-feed-advice.validation-error.description.piglets-farrowing-category',
      )}</li>`;
    }

    this.dialogService.confirmWarning({
      title: this.locale.translate('sows-feed-advice.validation-error.title'),
      text: `<ul>${description}</ul>`,
    });

    return false;
  }
}
